<script setup lang="ts">
import { SortMode } from "@lxc/app-device-common";
import { storeToRefs } from "pinia";
import { useSideNavigation } from "~/core/composables/useSideNavigation";
import type { CloseEvent } from "~/core/models/CloseEvent.interface";
import { formatIsoDate } from "~/core/utils/date";
import { useFetchAppInterface } from "~/modules/applicationManagement/composables/useFetchAppInterface";
import {
  PATH_PARAMETERS_APPLICATIONS_THIRD_PART_APP,
  PATH_PARAMETERS_APPLICATIONS_THIRD_PART_APP_CLIENT_ID,
} from "~/modules/applicationManagement/constants";
import type { Interface } from "~/modules/applicationManagement/models/Interface.interface";
import type { InterfaceAuthenticationType } from "~/modules/applicationManagement/models/InterfaceAuthenticationType.enum";
import type { InterfaceStatus } from "~/modules/applicationManagement/models/InterfaceStatus.enum";
import { canManageThirdPartyApps } from "~/modules/applicationManagement/utils/canManageThirdPartyApps";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useFetchProfiles } from "~/modules/user/composables/useFetchProfiles";
import { getProfileLabel } from "~/modules/user/utils/profileLabel";

const props = defineProps<{
  create: boolean;
  disableActivateInterfaceButton: boolean;
}>();
const emit = defineEmits(["update:create", "change"]);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { navigateToPath, onClose } = useSideNavigation();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const { isLoading, results, error, fetchData, onSearch, onSortChange } =
  useFetchAppInterface({
    organizationCode: currentOrganization.value?.code,
  });
const { results: userProfiles, fetchData: fetchAllProfiles } = useFetchProfiles(
  { organizationCode: currentOrganization.value?.code },
);

const profileCodes: Ref<string[]> = ref([]);
const selectedInterfaceClientId: Ref<string | undefined | null> = ref();

interface InterfaceListI {
  name: string;
  authenticationType: InterfaceAuthenticationType;
  profileName: string;
  updatedAt: string;
  status: InterfaceStatus;
  clientId?: string;
}

async function getProfilesCodes() {
  profileCodes.value = results.value
    ? results.value?.results.map(
        (appInterface: Interface) => appInterface.profileCode,
      )
    : [];
}

async function getUserProfiles() {
  await fetchAllProfiles();
}

async function loadData(page?: number, pageSize?: number) {
  if (page && pageSize) {
    await fetchData(page, pageSize);
  } else {
    await fetchData(
      results.value?.pagination.page,
      results.value?.pagination.pageSize,
    );
  }
  await getProfilesCodes();
  await getUserProfiles();
}

async function onInterfaceStatusChange() {
  await loadData();
  onInterfaceChange();
}

const interfacesList: ComputedRef<InterfaceListI[] | undefined> = computed(
  () => {
    return results.value?.results.map((appInterface: Interface) => {
      const profile = userProfiles.value?.find(
        (profile) => profile.id === appInterface.profileCode,
      );
      return {
        name: appInterface.name,
        authenticationType: appInterface.authenticationType,
        profileName: profile?.name ?? "",
        updatedAt: appInterface.updatedAt,
        status: appInterface.status,
        clientId: appInterface.clientId,
      };
    });
  },
);

const onSelectInterface = async (selectedInterface: InterfaceListI) => {
  if (selectedInterface.clientId && canManageThirdPartyApps()) {
    await navigateToPath(
      router.resolve({
        name: PATH_PARAMETERS_APPLICATIONS_THIRD_PART_APP_CLIENT_ID,
        params: { clientId: selectedInterface.clientId },
      }).href,
    );
  }
};

const onCloseInterfaceDetail = async (event: CloseEvent) => {
  // reset to undefined because the URL of the list and the creation are the same, otherwise it stays at null
  selectedInterfaceClientId.value = undefined;

  await onClose(event, PATH_PARAMETERS_APPLICATIONS_THIRD_PART_APP, loadData);
};

const initDisplayFromRoute = () => {
  if (route.path.startsWith(PATH_PARAMETERS_APPLICATIONS_THIRD_PART_APP)) {
    selectedInterfaceClientId.value =
      (route.params.clientId as string) || undefined;
  }
};

const onInterfaceChange = () => {
  emit("change");
};

watch(
  () => props.create,
  () => {
    selectedInterfaceClientId.value = null;
    emit("update:create", false);
  },
);
watch(route, initDisplayFromRoute);

onSearch(async () => {
  await loadData();
  initDisplayFromRoute();
});
</script>
<template>
  <lxc-table
    min-width="84rem"
    :is-loading="isLoading"
    :data="interfacesList"
    :context="results?.pagination"
    :error="error?.toError()"
    data-cy="interface-table"
    :clickable="canManageThirdPartyApps()"
    :empty-text="t('applicationMgt.tabs.thirdPartyApp.empty')"
    class="mt-2"
    @change-page-and-page-size="loadData"
    @row-click="onSelectInterface"
    @sort-change="onSortChange"
  >
    <!-- ! Organization Name will be accessible in the future, for now we only get the organization code
    <lxc-table-column
      prop="organizationName"
      :label="t('applicationMgt.tabs.thirdPartyApp.column.organizationName')"
    /> -->
    <lxc-table-column
      prop="name"
      :label="t('applicationMgt.tabs.thirdPartyApp.column.interfaceName')"
      :sort-mode="SortMode.CUSTOM"
      min-width="15rem"
    />
    <lxc-table-column
      prop="authenticationType"
      :label="t('applicationMgt.tabs.thirdPartyApp.column.authMethod')"
      width="18rem"
    >
      <template #default="scope">
        {{
          scope.row.authenticationType
            ? t(
                `applicationMgt.tabs.thirdPartyApp.authMethod.${scope.row.authenticationType}`,
              )
            : ""
        }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      prop="profileName"
      :label="t('applicationMgt.tabs.thirdPartyApp.column.profile')"
      width="12rem"
    >
      <template #default="scope">
        {{ getProfileLabel({ name: scope.row.profileName }) }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      prop="updatedAt"
      :label="t('applicationMgt.tabs.thirdPartyApp.column.updatedAt')"
      :sort-mode="SortMode.CUSTOM"
      width="15rem"
    >
      <template #default="scope">
        {{ formatIsoDate(scope.row.updatedAt, t("dateFormat.datetime")) }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      prop="status"
      :label="t('applicationMgt.tabs.thirdPartyApp.column.status')"
      class="!py-0"
      width="12rem"
    >
      <template #default="scope">
        <third-party-application-status-badge :status="scope.row.status" />
      </template>
    </lxc-table-column>
    <lxc-table-column
      v-if="canManageThirdPartyApps()"
      class="!py-0"
      width="12rem"
    >
      <template #default="scope">
        <div class="flex">
          <third-party-application-activate-deactivate-action
            :interface-client-id="scope.row.clientId"
            :interface-name="scope.row.name"
            :interface-status="scope.row.status"
            :can-manage-third-party-app="canManageThirdPartyApps()"
            :disable-activate-interface-button="disableActivateInterfaceButton"
            @change="onInterfaceStatusChange"
          />
          <third-party-application-delete-action
            :interface-client-id="scope.row.clientId"
            :interface-name="scope.row.name"
            :can-manage-third-party-apps="canManageThirdPartyApps()"
            @deleted="onInterfaceStatusChange"
          />
        </div>
      </template>
    </lxc-table-column>
  </lxc-table>
  <third-party-application-form
    :selected-interface-client-id="selectedInterfaceClientId"
    @close="onCloseInterfaceDetail"
    @created="onInterfaceChange"
  />
</template>
<style lang="scss" scoped>
:deep(table) {
  tbody {
    tr {
      &:hover {
        button {
          visibility: visible;
        }
      }
    }
  }
}
</style>
